// Canonical ingredient taxonomy, the allowlist every other module refers to.
//
// Safety model (research C4, guardrails #2 and #3):
//   * Detections, recipes, staples, substitutions and the grocery list all use these ids.
//   * Diet/allergen violations are DERIVED from the flags below (see diet.ts); recipes are
//     never hand-tagged. Anything not in this table cannot reach ranking.
//   * Ambiguous items (e.g. "sausage", could be pork) fail closed.

export type FoodCategory =
  | 'protein' | 'vegetable' | 'fruit' | 'dairy' | 'grain'
  | 'pantry' | 'spice' | 'condiment' | 'frozen' | 'other';

export type Aisle =
  | 'produce' | 'meat_seafood' | 'dairy_eggs' | 'bakery' | 'pantry'
  | 'spices' | 'condiments' | 'frozen' | 'other';

/** US "big 9" allergens (FASTER Act). */
export type Allergen =
  | 'milk' | 'egg' | 'fish' | 'shellfish' | 'tree_nut' | 'peanut' | 'wheat' | 'soy' | 'sesame';

export const ALLERGENS: { id: Allergen; label: string }[] = [
  { id: 'milk', label: 'Milk / dairy' },
  { id: 'egg', label: 'Egg' },
  { id: 'peanut', label: 'Peanut' },
  { id: 'tree_nut', label: 'Tree nuts' },
  { id: 'wheat', label: 'Wheat' },
  { id: 'soy', label: 'Soy' },
  { id: 'fish', label: 'Fish' },
  { id: 'shellfish', label: 'Shellfish' },
  { id: 'sesame', label: 'Sesame' },
];

export interface IngredientDef {
  name: string;
  category: FoodCategory;
  aisle: Aisle;
  /** One-way generalisation: an item satisfies any ancestor ("cherry tomatoes" → "tomatoes"). */
  parent?: string;
  allergens?: Allergen[];
  /** Animal flesh (any land animal). */
  meat?: 'poultry' | 'beef' | 'pork' | 'lamb' | 'unknown';
  seafood?: 'fish' | 'shellfish';
  /** Any animal-derived product (dairy, egg, honey, gelatin…). Meat/seafood imply this. */
  animal?: boolean;
  gluten?: 'yes' | 'may';
  alcohol?: boolean;
  /** Commonly made with animal rennet, vegetarians should check the label. */
  rennet?: boolean;
  aliases?: string[];
  /** Search term used for the grocery handoff when it differs from `name`. */
  shopAs?: string;
}

const P = (name: string, extra: Partial<IngredientDef> = {}): IngredientDef =>
  ({ name, category: 'vegetable', aisle: 'produce', ...extra });

const RAW = {
  // ── Poultry / meat ────────────────────────────────────────────────────────
  chicken: { name: 'chicken', category: 'protein', aisle: 'meat_seafood', meat: 'poultry', aliases: ['whole chicken', 'rotisserie chicken', 'cooked chicken'] },
  chicken_breast: { name: 'chicken breast', category: 'protein', aisle: 'meat_seafood', meat: 'poultry', parent: 'chicken', aliases: ['chicken breasts', 'chicken fillet', 'chicken fillets'] },
  chicken_thighs: { name: 'chicken thighs', category: 'protein', aisle: 'meat_seafood', meat: 'poultry', parent: 'chicken', aliases: ['chicken thigh'] },
  ground_turkey: { name: 'ground turkey', category: 'protein', aisle: 'meat_seafood', meat: 'poultry', aliases: ['turkey mince', 'minced turkey'] },
  ground_beef: { name: 'ground beef', category: 'protein', aisle: 'meat_seafood', meat: 'beef', aliases: ['beef mince', 'minced beef', 'hamburger meat'] },
  steak: { name: 'steak', category: 'protein', aisle: 'meat_seafood', meat: 'beef', aliases: ['beef steak', 'sirloin'] },
  bacon: { name: 'bacon', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['streaky bacon'] },
  ham: { name: 'ham', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['sliced ham'] },
  pork_chops: { name: 'pork chops', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['pork chop'] },
  // "Sausage" without a stated meat is ambiguous → fails closed for veg/no-pork/no-beef/halal.
  sausage: { name: 'sausage', category: 'protein', aisle: 'meat_seafood', meat: 'unknown', aliases: ['sausages', 'breakfast sausage', 'links'] },
  chicken_drumsticks: { name: 'chicken drumsticks', category: 'protein', aisle: 'meat_seafood', meat: 'poultry', parent: 'chicken', aliases: ['drumsticks', 'chicken legs'] },
  pork_shoulder: { name: 'pork shoulder', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['pork butt', 'boston butt'] },
  beef_chuck: { name: 'beef chuck', category: 'protein', aisle: 'meat_seafood', meat: 'beef', aliases: ['chuck roast', 'stew beef', 'beef stew meat'] },
  ground_pork: { name: 'ground pork', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['pork mince', 'minced pork'] },
  ground_lamb: { name: 'ground lamb', category: 'protein', aisle: 'meat_seafood', meat: 'lamb', aliases: ['lamb mince', 'minced lamb'] },
  lamb: { name: 'lamb', category: 'protein', aisle: 'meat_seafood', meat: 'lamb', aliases: ['lamb shoulder', 'lamb stew meat', 'leg of lamb'] },
  chorizo: { name: 'chorizo', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['spanish chorizo', 'chorizo sausage'] },
  prosciutto: { name: 'prosciutto', category: 'protein', aisle: 'meat_seafood', meat: 'pork', aliases: ['parma ham'] },
  turkey_breast: { name: 'turkey breast', category: 'protein', aisle: 'meat_seafood', meat: 'poultry', aliases: ['turkey breast roast', 'turkey cutlets'] },
  // ── Seafood ───────────────────────────────────────────────────────────────
  shrimp: { name: 'shrimp', category: 'protein', aisle: 'meat_seafood', seafood: 'shellfish', allergens: ['shellfish'], aliases: ['prawns', 'prawn'] },
  salmon: { name: 'salmon', category: 'protein', aisle: 'meat_seafood', seafood: 'fish', allergens: ['fish'], aliases: ['salmon fillet'] },
  tuna: { name: 'canned tuna', category: 'protein', aisle: 'pantry', seafood: 'fish', allergens: ['fish'], aliases: ['tuna', 'tinned tuna', 'tuna can'] },
  white_fish: { name: 'white fish', category: 'protein', aisle: 'meat_seafood', seafood: 'fish', allergens: ['fish'], aliases: ['cod', 'tilapia', 'haddock'] },
  // ── Plant protein ─────────────────────────────────────────────────────────
  tofu: { name: 'tofu', category: 'protein', aisle: 'produce', allergens: ['soy'], aliases: ['firm tofu'] },
  black_beans: { name: 'black beans', category: 'protein', aisle: 'pantry', aliases: ['canned black beans'] },
  chickpeas: { name: 'chickpeas', category: 'protein', aisle: 'pantry', aliases: ['garbanzo beans', 'garbanzos'] },
  lentils: { name: 'lentils', category: 'protein', aisle: 'pantry', aliases: ['red lentils', 'green lentils'] },
  eggs: { name: 'eggs', category: 'protein', aisle: 'dairy_eggs', animal: true, allergens: ['egg'], aliases: ['egg', 'large eggs'] },
  kidney_beans: { name: 'kidney beans', category: 'protein', aisle: 'pantry', aliases: ['red kidney beans', 'red beans'] },
  split_peas: { name: 'split peas', category: 'protein', aisle: 'pantry', aliases: ['green split peas', 'yellow split peas'] },
  edamame: { name: 'edamame', category: 'protein', aisle: 'frozen', allergens: ['soy'], aliases: ['soybeans', 'soy beans'] },
  white_beans: { name: 'white beans', category: 'protein', aisle: 'pantry', aliases: ['cannellini beans', 'great northern beans', 'navy beans'] },
  fava_beans: { name: 'fava beans', category: 'protein', aisle: 'pantry', aliases: ['broad beans', 'ful', 'foul'] },
  pinto_beans: { name: 'pinto beans', category: 'protein', aisle: 'pantry', aliases: ['pinto bean'] },
  // ── Dairy ─────────────────────────────────────────────────────────────────
  milk: { name: 'milk', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], aliases: ['whole milk', 'skim milk', '2% milk', 'semi-skimmed milk'] },
  heavy_cream: { name: 'heavy cream', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], aliases: ['double cream', 'whipping cream', 'cream'] },
  sour_cream: { name: 'sour cream', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'] },
  yogurt: { name: 'yogurt', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], aliases: ['yoghurt', 'plain yogurt'] },
  greek_yogurt: { name: 'greek yogurt', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], parent: 'yogurt', aliases: ['greek yoghurt'] },
  butter: { name: 'butter', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], aliases: ['salted butter', 'unsalted butter'] },
  cheese: { name: 'cheese', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'] },
  cheddar: { name: 'cheddar', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], parent: 'cheese', aliases: ['cheddar cheese', 'shredded cheddar'] },
  mozzarella: { name: 'mozzarella', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], parent: 'cheese', aliases: ['mozzarella cheese'] },
  parmesan: { name: 'parmesan', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], rennet: true, parent: 'cheese', aliases: ['parmigiano', 'parmigiano-reggiano', 'parmesan cheese'] },
  feta: { name: 'feta', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], parent: 'cheese', aliases: ['feta cheese'] },
  cream_cheese: { name: 'cream cheese', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'] },
  buttermilk: { name: 'buttermilk', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'] },
  blue_cheese: { name: 'blue cheese', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], rennet: true, parent: 'cheese', aliases: ['bleu cheese', 'blue cheese crumbles'] },
  provolone: { name: 'provolone', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], rennet: true, parent: 'cheese', aliases: ['provolone cheese'] },
  ricotta: { name: 'ricotta', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], parent: 'cheese', aliases: ['ricotta cheese'] },
  gruyere: { name: 'Gruyère', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], rennet: true, parent: 'cheese', aliases: ['gruyere', 'gruyere cheese', 'swiss cheese'] },
  paneer: { name: 'paneer', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], parent: 'cheese', aliases: ['paneer cheese'] },
  ghee: { name: 'ghee', category: 'dairy', aisle: 'pantry', animal: true, allergens: ['milk'], aliases: ['clarified butter'] },
  halloumi: { name: 'halloumi', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], rennet: true, parent: 'cheese', aliases: ['halloumi cheese'] },
  queso_fresco: { name: 'queso fresco', category: 'dairy', aisle: 'dairy_eggs', animal: true, allergens: ['milk'], rennet: true, parent: 'cheese', aliases: ['cotija', 'mexican cheese'] },
  // ── Grains / bakery ───────────────────────────────────────────────────────
  rice: { name: 'rice', category: 'grain', aisle: 'pantry' },
  white_rice: { name: 'white rice', category: 'grain', aisle: 'pantry', parent: 'rice', aliases: ['jasmine rice', 'basmati rice', 'long grain rice'] },
  brown_rice: { name: 'brown rice', category: 'grain', aisle: 'pantry', parent: 'rice' },
  leftover_rice: { name: 'leftover rice', category: 'grain', aisle: 'other', parent: 'rice', aliases: ['cooked rice', 'day-old rice'] },
  pasta: { name: 'pasta', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['penne', 'fusilli', 'macaroni', 'linguine', 'fettuccine', 'rigatoni', 'ziti', 'orzo', 'lasagna noodles'] },
  spaghetti: { name: 'spaghetti', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', parent: 'pasta' },
  bread: { name: 'bread', category: 'grain', aisle: 'bakery', allergens: ['wheat'], gluten: 'yes', aliases: ['sliced bread', 'sandwich bread', 'loaf', 'sourdough'] },
  flour_tortillas: { name: 'flour tortillas', category: 'grain', aisle: 'bakery', allergens: ['wheat'], gluten: 'yes', aliases: ['tortillas', 'tortilla', 'wraps', 'tortilla wraps'] },
  corn_tortillas: { name: 'corn tortillas', category: 'grain', aisle: 'bakery' },
  flour: { name: 'flour', category: 'pantry', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['all-purpose flour', 'plain flour'] },
  oats: { name: 'oats', category: 'grain', aisle: 'pantry', gluten: 'may', aliases: ['rolled oats', 'oatmeal', 'porridge oats'] },
  quinoa: { name: 'quinoa', category: 'grain', aisle: 'pantry' },
  hamburger_buns: { name: 'hamburger buns', category: 'grain', aisle: 'bakery', allergens: ['wheat'], gluten: 'yes', aliases: ['burger buns', 'buns'] },
  hoagie_rolls: { name: 'hoagie rolls', category: 'grain', aisle: 'bakery', allergens: ['wheat'], gluten: 'yes', aliases: ['sub rolls', 'hoagie buns', 'sandwich rolls'] },
  breadcrumbs: { name: 'breadcrumbs', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['bread crumbs', 'panko'] },
  egg_noodles: { name: 'egg noodles', category: 'grain', aisle: 'pantry', allergens: ['wheat', 'egg'], gluten: 'yes', animal: true },
  cornmeal: { name: 'cornmeal', category: 'grain', aisle: 'pantry', aliases: ['yellow cornmeal', 'corn meal'] },
  grits: { name: 'grits', category: 'grain', aisle: 'pantry', aliases: ['corn grits', 'hominy grits'] },
  pita_bread: { name: 'pita bread', category: 'grain', aisle: 'bakery', allergens: ['wheat'], gluten: 'yes', aliases: ['pita', 'pitta'] },
  gnocchi: { name: 'gnocchi', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['potato gnocchi'] },
  rice_noodles: { name: 'rice noodles', category: 'grain', aisle: 'pantry', aliases: ['rice sticks', 'pad thai noodles', 'vermicelli'] },
  ramen_noodles: { name: 'ramen noodles', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['ramen'] },
  udon_noodles: { name: 'udon noodles', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['udon'] },
  glass_noodles: { name: 'glass noodles', category: 'grain', aisle: 'pantry', aliases: ['cellophane noodles', 'sweet potato noodles', 'japchae noodles', 'mung bean noodles'] },
  // Many brands add egg; fail closed.
  dumpling_wrappers: { name: 'dumpling wrappers', category: 'grain', aisle: 'frozen', allergens: ['wheat', 'egg'], gluten: 'yes', animal: true, aliases: ['gyoza wrappers', 'wonton wrappers', 'potsticker wrappers'] },
  rice_paper: { name: 'rice paper', category: 'grain', aisle: 'pantry', aliases: ['rice paper wrappers', 'spring roll wrappers'] },
  bulgur: { name: 'bulgur', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes', aliases: ['bulgur wheat', 'bulghur', 'cracked wheat'] },
  couscous: { name: 'couscous', category: 'grain', aisle: 'pantry', allergens: ['wheat'], gluten: 'yes' },
  masa_harina: { name: 'masa harina', category: 'grain', aisle: 'pantry', aliases: ['masarepa', 'corn masa flour', 'arepa flour'] },
  // ── Vegetables ────────────────────────────────────────────────────────────
  spinach: P('spinach'),
  baby_spinach: P('baby spinach', { parent: 'spinach' }),
  kale: P('kale'),
  lettuce: P('lettuce', { aliases: ['romaine', 'iceberg lettuce', 'salad leaves'] }),
  tomatoes: P('tomatoes', { aliases: ['tomato', 'vine tomatoes'] }),
  cherry_tomatoes: P('cherry tomatoes', { parent: 'tomatoes', aliases: ['grape tomatoes'] }),
  cucumber: P('cucumber', { aliases: ['cucumbers'] }),
  bell_pepper: P('bell pepper', { aliases: ['bell peppers', 'peppers', 'capsicum', 'red pepper', 'green pepper', 'sweet pepper'] }),
  carrots: P('carrots', { aliases: ['carrot'] }),
  broccoli: P('broccoli'),
  cauliflower: P('cauliflower'),
  cabbage: P('cabbage'),
  celery: P('celery'),
  mushrooms: P('mushrooms', { aliases: ['mushroom', 'button mushrooms', 'cremini'] }),
  zucchini: P('zucchini', { aliases: ['courgette', 'courgettes'] }),
  green_beans: P('green beans'),
  corn: P('corn', { aliases: ['sweetcorn', 'corn on the cob'] }),
  scallions: P('scallions', { aliases: ['green onions', 'green onion', 'spring onions', 'spring onion'] }),
  ginger: P('ginger', { aliases: ['fresh ginger', 'ginger root'] }),
  garlic: P('garlic', { aliases: ['garlic cloves', 'garlic bulb'] }),
  onion: P('onion', { aliases: ['onions', 'yellow onion', 'white onion'] }),
  red_onion: P('red onion', { parent: 'onion' }),
  potatoes: P('potatoes', { aliases: ['potato'] }),
  sweet_potatoes: P('sweet potatoes', { aliases: ['sweet potato', 'yams'] }),
  leeks: P('leeks', { aliases: ['leek'] }),
  avocado: P('avocado', { category: 'fruit', aliases: ['avocados'] }),
  fresh_basil: P('fresh basil', { aliases: ['basil'] }),
  cilantro: P('cilantro', { aliases: ['coriander leaves', 'fresh coriander'] }),
  peas: P('peas'),
  frozen_peas: { name: 'frozen peas', category: 'frozen', aisle: 'frozen', parent: 'peas' },
  frozen_mixed_vegetables: { name: 'frozen mixed vegetables', category: 'frozen', aisle: 'frozen', aliases: ['frozen veg', 'mixed veg'] },
  fresh_parsley: { name: 'fresh parsley', category: 'vegetable', aisle: 'produce', aliases: ['parsley', 'flat-leaf parsley', 'italian parsley'] },
  eggplant: P('eggplant', { aliases: ['aubergine', 'eggplants'] }),
  beets: P('beets', { aliases: ['beetroot', 'beet'] }),
  fresh_dill: P('fresh dill', { aliases: ['dill'] }),
  bok_choy: P('bok choy', { aliases: ['pak choi', 'baby bok choy'] }),
  bean_sprouts: P('bean sprouts', { aliases: ['mung bean sprouts'] }),
  shiitake: P('shiitake mushrooms', { parent: 'mushrooms', aliases: ['shiitake'] }),
  chili_peppers: P('fresh chili peppers', { aliases: ['chili pepper', 'chilies', 'jalapeno', 'jalapenos', 'serrano', 'thai chili', 'bird eye chili'] }),
  fresh_mint: P('fresh mint', { aliases: ['mint', 'mint leaves'] }),
  lemongrass: P('lemongrass', { aliases: ['lemon grass'] }),
  tomatillos: P('tomatillos', { aliases: ['tomatillo'] }),
  asparagus: P('asparagus'),
  // ── Fruit ─────────────────────────────────────────────────────────────────
  strawberries: P('strawberries', { category: 'fruit', aliases: ['strawberry'] }),
  blueberries: P('blueberries', { category: 'fruit', aliases: ['blueberry'] }),
  frozen_berries: { name: 'frozen berries', category: 'frozen', aisle: 'frozen', aliases: ['frozen mixed berries'] },
  bananas: P('bananas', { category: 'fruit', aliases: ['banana'] }),
  apples: P('apples', { category: 'fruit', aliases: ['apple'] }),
  oranges: P('oranges', { category: 'fruit', aliases: ['orange'] }),
  grapes: P('grapes', { category: 'fruit' }),
  lemon: P('lemon', { category: 'fruit', aliases: ['lemons'] }),
  lime: P('lime', { category: 'fruit', aliases: ['limes'] }),
  pineapple: P('pineapple', { category: 'fruit' }),
  plantains: P('plantains', { category: 'fruit', aliases: ['plantain'] }),
  // ── Oils, pantry ──────────────────────────────────────────────────────────
  olive_oil: { name: 'olive oil', category: 'pantry', aisle: 'pantry', aliases: ['extra virgin olive oil'] },
  cooking_oil: { name: 'cooking oil', category: 'pantry', aisle: 'pantry', aliases: ['vegetable oil', 'canola oil', 'sunflower oil'] },
  sesame_oil: { name: 'sesame oil', category: 'pantry', aisle: 'pantry', allergens: ['sesame'], aliases: ['toasted sesame oil'] },
  sugar: { name: 'sugar', category: 'pantry', aisle: 'pantry', aliases: ['white sugar', 'granulated sugar'] },
  brown_sugar: { name: 'brown sugar', category: 'pantry', aisle: 'pantry' },
  honey: { name: 'honey', category: 'pantry', aisle: 'pantry', animal: true },
  baking_powder: { name: 'baking powder', category: 'pantry', aisle: 'pantry' },
  cornstarch: { name: 'cornstarch', category: 'pantry', aisle: 'pantry', aliases: ['cornflour', 'corn starch'] },
  vegetable_broth: { name: 'vegetable broth', category: 'pantry', aisle: 'pantry', aliases: ['vegetable stock', 'veg stock'] },
  coconut_milk: { name: 'coconut milk', category: 'pantry', aisle: 'pantry', aliases: ['canned coconut milk'] },
  peanut_butter: { name: 'peanut butter', category: 'pantry', aisle: 'pantry', allergens: ['peanut'] },
  almonds: { name: 'almonds', category: 'pantry', aisle: 'pantry', allergens: ['tree_nut'] },
  almond_milk: { name: 'almond milk', category: 'dairy', aisle: 'dairy_eggs', allergens: ['tree_nut'] },
  walnuts: { name: 'walnuts', category: 'pantry', aisle: 'pantry', allergens: ['tree_nut'] },
  chicken_broth: { name: 'chicken broth', category: 'pantry', aisle: 'pantry', meat: 'poultry', aliases: ['chicken stock'] },
  beef_broth: { name: 'beef broth', category: 'pantry', aisle: 'pantry', meat: 'beef', aliases: ['beef stock'] },
  maple_syrup: { name: 'maple syrup', category: 'pantry', aisle: 'pantry', aliases: ['pure maple syrup'] },
  canned_tomatoes: { name: 'canned tomatoes', category: 'pantry', aisle: 'pantry', aliases: ['diced tomatoes', 'whole peeled tomatoes', 'tinned tomatoes'] },
  olives: { name: 'olives', category: 'pantry', aisle: 'pantry', aliases: ['kalamata olives', 'black olives', 'green olives'] },
  capers: { name: 'capers', category: 'pantry', aisle: 'pantry' },
  pine_nuts: { name: 'pine nuts', category: 'pantry', aisle: 'pantry', allergens: ['tree_nut'], aliases: ['pignoli'] },
  tahini: { name: 'tahini', category: 'pantry', aisle: 'pantry', allergens: ['sesame'], aliases: ['sesame paste'] },
  sesame_seeds: { name: 'sesame seeds', category: 'pantry', aisle: 'pantry', allergens: ['sesame'] },
  peanuts: { name: 'peanuts', category: 'pantry', aisle: 'pantry', allergens: ['peanut'], aliases: ['roasted peanuts'] },
  cashews: { name: 'cashews', category: 'pantry', aisle: 'pantry', allergens: ['tree_nut'], aliases: ['cashew nuts'] },
  // ── Spices ────────────────────────────────────────────────────────────────
  salt: { name: 'salt', category: 'spice', aisle: 'spices', aliases: ['sea salt', 'kosher salt'] },
  black_pepper: { name: 'black pepper', category: 'spice', aisle: 'spices', aliases: ['pepper', 'ground pepper', 'peppercorns'] },
  cumin: { name: 'cumin', category: 'spice', aisle: 'spices', aliases: ['ground cumin'] },
  paprika: { name: 'paprika', category: 'spice', aisle: 'spices', aliases: ['smoked paprika'] },
  oregano: { name: 'oregano', category: 'spice', aisle: 'spices', aliases: ['dried oregano'] },
  chili_flakes: { name: 'chili flakes', category: 'spice', aisle: 'spices', aliases: ['red pepper flakes', 'chilli flakes', 'crushed red pepper'] },
  curry_powder: { name: 'curry powder', category: 'spice', aisle: 'spices', aliases: ['curry spice'] },
  cinnamon: { name: 'cinnamon', category: 'spice', aisle: 'spices', aliases: ['ground cinnamon'] },
  thyme: { name: 'thyme', category: 'spice', aisle: 'spices', aliases: ['dried thyme'] },
  chili_powder: { name: 'chili powder', category: 'spice', aisle: 'spices', aliases: ['chile powder'] },
  garlic_powder: { name: 'garlic powder', category: 'spice', aisle: 'spices', aliases: ['granulated garlic'] },
  onion_powder: { name: 'onion powder', category: 'spice', aisle: 'spices' },
  rosemary: { name: 'rosemary', category: 'spice', aisle: 'spices', aliases: ['dried rosemary', 'fresh rosemary'] },
  garam_masala: { name: 'garam masala', category: 'spice', aisle: 'spices' },
  turmeric: { name: 'turmeric', category: 'spice', aisle: 'spices', aliases: ['ground turmeric'] },
  ground_coriander: { name: 'ground coriander', category: 'spice', aisle: 'spices', aliases: ['coriander powder'] },
  bay_leaves: { name: 'bay leaves', category: 'spice', aisle: 'spices', aliases: ['bay leaf'] },
  sumac: { name: 'sumac', category: 'spice', aisle: 'spices', aliases: ['ground sumac'] },
  // ── Condiments ────────────────────────────────────────────────────────────
  soy_sauce: { name: 'soy sauce', category: 'condiment', aisle: 'condiments', allergens: ['soy', 'wheat'], gluten: 'yes', aliases: ['soya sauce', 'shoyu'] },
  vinegar: { name: 'vinegar', category: 'condiment', aisle: 'condiments', aliases: ['white vinegar', 'rice vinegar', 'apple cider vinegar'] },
  tomato_paste: { name: 'tomato paste', category: 'condiment', aisle: 'pantry', aliases: ['tomato puree'] },
  tomato_sauce: { name: 'tomato sauce', category: 'condiment', aisle: 'pantry', aliases: ['passata', 'crushed tomatoes'] },
  marinara: { name: 'marinara sauce', category: 'condiment', aisle: 'pantry', parent: 'tomato_sauce', aliases: ['marinara', 'pasta sauce', 'red pasta sauce'] },
  salsa: { name: 'salsa', category: 'condiment', aisle: 'condiments' },
  ketchup: { name: 'ketchup', category: 'condiment', aisle: 'condiments', aliases: ['tomato ketchup'] },
  mustard: { name: 'mustard', category: 'condiment', aisle: 'condiments', aliases: ['dijon mustard', 'yellow mustard'] },
  mayonnaise: { name: 'mayonnaise', category: 'condiment', aisle: 'condiments', animal: true, allergens: ['egg'], aliases: ['mayo'] },
  hot_sauce: { name: 'hot sauce', category: 'condiment', aisle: 'condiments', aliases: ['sriracha', 'tabasco'] },
  jam: { name: 'jam', category: 'condiment', aisle: 'condiments', aliases: ['jelly', 'preserves'] },
  // Made with anchovies: fish allergen, not vegetarian.
  worcestershire_sauce: { name: 'Worcestershire sauce', category: 'condiment', aisle: 'condiments', allergens: ['fish'], seafood: 'fish', aliases: ['worcestershire'] },
  bbq_sauce: { name: 'barbecue sauce', category: 'condiment', aisle: 'condiments', aliases: ['bbq sauce', 'barbeque sauce'] },
  balsamic_vinegar: { name: 'balsamic vinegar', category: 'condiment', aisle: 'condiments', aliases: ['balsamic'] },
  // Fermented soybean pastes and sauces: soy, and usually wheat (fail closed).
  miso: { name: 'miso', category: 'condiment', aisle: 'condiments', allergens: ['soy'], gluten: 'may', aliases: ['miso paste', 'white miso'] },
  gochujang: { name: 'gochujang', category: 'condiment', aisle: 'condiments', allergens: ['soy', 'wheat'], gluten: 'yes', aliases: ['korean chili paste', 'korean red pepper paste'] },
  hoisin_sauce: { name: 'hoisin sauce', category: 'condiment', aisle: 'condiments', allergens: ['soy', 'wheat'], gluten: 'may', aliases: ['hoisin'] },
  chili_bean_paste: { name: 'chili bean paste', category: 'condiment', aisle: 'condiments', allergens: ['soy', 'wheat'], gluten: 'yes', aliases: ['doubanjiang', 'toban djan'] },
  fish_sauce: { name: 'fish sauce', category: 'condiment', aisle: 'condiments', allergens: ['fish'], seafood: 'fish', aliases: ['nam pla', 'nuoc mam'] },
  oyster_sauce: { name: 'oyster sauce', category: 'condiment', aisle: 'condiments', allergens: ['shellfish', 'wheat'], seafood: 'shellfish', gluten: 'may' },
  // Usually made with fish sauce or salted shrimp; fail closed.
  kimchi: { name: 'kimchi', category: 'condiment', aisle: 'condiments', allergens: ['fish', 'shellfish'], seafood: 'shellfish', aliases: ['kimchee'] },
  // Many brands contain shrimp paste; fail closed.
  red_curry_paste: { name: 'red curry paste', category: 'condiment', aisle: 'condiments', allergens: ['shellfish'], seafood: 'shellfish', aliases: ['thai red curry paste'] },
  green_curry_paste: { name: 'green curry paste', category: 'condiment', aisle: 'condiments', allergens: ['shellfish'], seafood: 'shellfish', aliases: ['thai green curry paste'] },
  // ── Other (detectable, never used by the corpus) ─────────────────────────
  beer: { name: 'beer', category: 'other', aisle: 'other', alcohol: true, gluten: 'yes' }, // brewed from barley; gluten-removed beer still fails closed
  wine: { name: 'wine', category: 'other', aisle: 'other', alcohol: true, aliases: ['white wine', 'red wine'] },
  orange_juice: { name: 'orange juice', category: 'other', aisle: 'other', aliases: ['oj'] },
} satisfies Record<string, IngredientDef>;

export type IngredientId = keyof typeof RAW;
export const INGREDIENTS: Record<IngredientId, IngredientDef> = RAW;
export const INGREDIENT_IDS = Object.keys(RAW) as IngredientId[];

export function isIngredientId(x: unknown): x is IngredientId {
  return typeof x === 'string' && Object.prototype.hasOwnProperty.call(RAW, x);
}

export function ingredient(id: IngredientId): IngredientDef {
  return INGREDIENTS[id];
}

export function displayName(id: IngredientId): string {
  return INGREDIENTS[id].name;
}

/** The id itself followed by every ancestor (child → root). */
/** Sentence-case name for labels and list items ("Olive oil"). */
export function labelName(id: IngredientId): string {
  const n = displayName(id);
  return n ? n[0].toUpperCase() + n.slice(1) : n;
}

export function lineage(id: IngredientId): IngredientId[] {
  const out: IngredientId[] = [];
  let cur: string | undefined = id;
  while (cur && isIngredientId(cur) && !out.includes(cur)) {
    out.push(cur);
    cur = INGREDIENTS[cur].parent;
  }
  return out;
}

// ── Name resolution (exact, alias, simple plural), never substring ─────────

export function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9%\- ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const NAME_INDEX: Map<string, IngredientId> = (() => {
  const m = new Map<string, IngredientId>();
  for (const id of INGREDIENT_IDS) {
    const def: IngredientDef = INGREDIENTS[id];
    const keys = [id.replace(/_/g, ' '), def.name, ...(def.aliases ?? [])];
    for (const k of keys) {
      const n = normalizeName(k);
      if (m.has(n) && m.get(n) !== id) throw new Error(`Duplicate ingredient alias "${n}"`);
      m.set(n, id);
    }
  }
  return m;
})();

function singularCandidates(n: string): string[] {
  const out: string[] = [];
  if (n.endsWith('ies')) out.push(n.slice(0, -3) + 'y');
  if (n.endsWith('oes')) out.push(n.slice(0, -2));
  if (n.endsWith('es')) out.push(n.slice(0, -2));
  if (n.endsWith('s')) out.push(n.slice(0, -1));
  out.push(n + 's');
  return out;
}

/** Resolve free text (a detection label or something the user typed) to a canonical id. */
export function resolveIngredient(text: string): IngredientId | null {
  const n = normalizeName(text);
  if (!n) return null;
  const direct = NAME_INDEX.get(n);
  if (direct) return direct;
  for (const c of singularCandidates(n)) {
    const hit = NAME_INDEX.get(c);
    if (hit) return hit;
  }
  return null;
}

/** Prefix/word search for autocomplete. Returns canonical ids, best first. */
export function searchIngredients(query: string, limit = 8): IngredientId[] {
  const q = normalizeName(query);
  if (!q) return [];
  const scored: { id: IngredientId; score: number }[] = [];
  const seen = new Set<IngredientId>();
  for (const [key, id] of NAME_INDEX) {
    let score = -1;
    if (key === q) score = 0;
    else if (key.startsWith(q)) score = 1;
    else if (key.split(' ').some((w) => w.startsWith(q))) score = 2;
    if (score >= 0) {
      const prev = scored.find((s) => s.id === id);
      if (prev) prev.score = Math.min(prev.score, score);
      else if (!seen.has(id)) { scored.push({ id, score }); seen.add(id); }
    }
  }
  return scored
    .sort((a, b) => a.score - b.score || displayName(a.id).length - displayName(b.id).length)
    .slice(0, limit)
    .map((s) => s.id);
}

export const AISLE_LABELS: Record<Aisle, string> = {
  produce: 'Produce',
  meat_seafood: 'Meat & seafood',
  dairy_eggs: 'Dairy & eggs',
  bakery: 'Bakery',
  pantry: 'Pantry',
  spices: 'Spices',
  condiments: 'Sauces & condiments',
  frozen: 'Frozen',
  other: 'Other',
};

export const AISLE_ORDER: Aisle[] = [
  'produce', 'meat_seafood', 'dairy_eggs', 'bakery', 'pantry', 'condiments', 'spices', 'frozen', 'other',
];
